import { useMemo } from 'react';
import { BrandingMode } from 'enums';
import { messages } from 'i18n';
import { SplitButtonOption } from 'components/SplitButton';
import { QuoteFormRoutes } from 'broker/broker-routes';

export function useGetQuoteSubmitOptions({
  activeStep,
  isFinalStep,
  quoteFormSteps,
  disabledButtonText,
  handleChangeStep,
  submitFormWrapper,
  isClientBranded = false,
  isDirectPlacement,
  withFormValidation,
  detailsExtraValidation,
}: {
  activeStep: QuoteFormRoutes;
  isDirectPlacement: boolean;
  isFinalStep: boolean;
  quoteFormSteps: QuoteFormRoutes[];
  flowQuoteIds: string[];
  disabledButtonText?: string;
  handleChangeStep: (step: QuoteFormRoutes, direction: 'next' | 'prev', brandingMode?: BrandingMode) => void;
  detailsExtraValidation: () => boolean;
  withFormValidation: (callback: () => void, extraValidation?: () => boolean) => Promise<void>;
  submitFormWrapper: (generateFlowQuote?: boolean, saveAsDoc?: boolean) => Promise<void>;
  isClientBranded: boolean;
}): SplitButtonOption[] {
  return useMemo(() => {
    const getQuoteSubmitButtonText = () => {
      if (isFinalStep) {
        return messages.addQuotePage.buttons.savePDFQuote;
      }
      const nextStep = quoteFormSteps[quoteFormSteps.indexOf(activeStep) + 1];
      switch (nextStep) {
        case QuoteFormRoutes.Insights:
          return messages.addQuotePage.buttons.nextCreateInsights;
        case QuoteFormRoutes.FlowQuote:
          return messages.addQuotePage.buttons.nextFlowQuote;
        default:
          return '';
      }
    };

    const options: SplitButtonOption[] = [
      {
        label: getQuoteSubmitButtonText(),
        disabled: disabledButtonText !== undefined,
        onClick: async () => {
          switch (activeStep) {
            case QuoteFormRoutes.FlowQuote:
              await submitFormWrapper();
              break;
            case QuoteFormRoutes.Details:
              await withFormValidation(() => handleChangeStep(activeStep, 'next'), detailsExtraValidation);
              break;
            default:
              handleChangeStep(activeStep, 'next');
              break;
          }
        },
      },
    ];

    if (isFinalStep && activeStep === QuoteFormRoutes.FlowQuote) {
      options.push({
        label: messages.addQuotePage.buttons.saveDOCXQuote,
        onClick: async () => {
          await submitFormWrapper(false, true);
        },
      });
    }

    if (activeStep === QuoteFormRoutes.Insights && !isFinalStep && isClientBranded) {
      options.push({
        label: messages.addQuotePage.buttons.nextBrandedQuote,
        onClick: () => {
          handleChangeStep(activeStep, 'next', BrandingMode.Organization);
        },
      });
    }
    return isDirectPlacement ? options.reverse() : options;
  }, [
    disabledButtonText,
    isFinalStep,
    activeStep,
    isClientBranded,
    isDirectPlacement,
    quoteFormSteps,
    submitFormWrapper,
    withFormValidation,
    detailsExtraValidation,
    handleChangeStep,
  ]);
}
